import { useSyncExternalStore } from 'react';
import type { QueryClient } from '@tanstack/react-query';

/**
 * Os tratadores de tempo real que os MÓDULOS registram — para `lib/realtime.tsx`
 * não precisar conhecer módulo nenhum.
 *
 * O socket é um só, aberto em `Layout.tsx`. Os avisos antigos (demanda,
 * pagamento, intervalo) estão escritos lá dentro; o do ERP é o primeiro que
 * mora no módulo dono (`modules/erp/live.ts`), e chega por aqui. Quem recebe
 * é `socket.onAny`, então um módulo que registre depois de a conexão abrir
 * continua recebendo — não há inscrição a refazer.
 */
type Handler = (payload: unknown, client: QueryClient) => void;

const handlers = new Map<string, Set<Handler>>();

export function registerRealtimeHandler(event: string, handler: Handler): () => void {
  let set = handlers.get(event);
  if (!set) {
    set = new Set();
    handlers.set(event, set);
  }
  set.add(handler);
  return () => set.delete(handler);
}

export function dispatchRealtime(
  event: string,
  payload: unknown,
  client: QueryClient,
): void {
  for (const handler of handlers.get(event) ?? []) {
    try {
      handler(payload, client);
    } catch (error) {
      // um tratador quebrado não pode calar os outros
      console.error(`tratador de tempo real de "${event}" falhou`, error);
    }
  }
}

/* ------------------------------------------------------------------------- *
 * O estado da conexão
 *
 * É o que deixa a tela trocar o polling curto pelo de reserva: conectado, o
 * aviso chega sozinho e reler a cada 15s é chamada à toa; desconectado, a tela
 * volta a perguntar no ritmo de antes. O estado nasce `false` — sem socket, a
 * tela se comporta como sempre se comportou.
 * ------------------------------------------------------------------------- */

let connected = false;
const listeners = new Set<() => void>();

export function setRealtimeConnected(value: boolean): void {
  if (connected === value) return;
  connected = value;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** `true` quando o socket está de pé e o servidor já decidiu as salas (`pronto`). */
export function useRealtimeConnected(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => connected,
    () => false,
  );
}
